import React, { type ForwardedRef, type HTMLAttributes, type JSX } from 'react';
import styled, { css } from 'styled-components';
import { Picture } from '@/app/components/hotel-detail/ImageWithFallback';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { focusRing } from '@/app/components/search/styles/mixins';

export type ImageSlideProps = {
  url: string;
  sources?: { srcSet: string; media?: string }[];
  loading?: 'eager' | 'lazy';
  overlay?: JSX.Element | null;
  containerRef?: ForwardedRef<HTMLDivElement>;
} & HTMLAttributes<HTMLDivElement>;

const ImageSlideOverlay = styled.div`
  > * {
    position: absolute;
  }
`;

/**
 * Single Slide of the gallery. Handles scroll snapping etc.
 */
function ImageSlide(props: ImageSlideProps) {
  const { url, sources, loading = 'lazy', overlay, containerRef, ...rest } = props;

  return (
    <div
      {...rest}
      ref={containerRef}
      css={css`
        flex: 0 0 calc(100vw - 32px);
        scroll-snap-align: center;
        scroll-snap-stop: always;
        position: relative;

        @media ${customCss.breakpoints.xs} {
          flex: 0 0 calc(100vw - 36px);
        }

        @media ${customCss.breakpoints.sm} {
          -webkit-transform: translate3d(0, 0, 0);
        }

        &:focus-visible {
          ${focusRing}
        }

        @media ${customCss.breakpoints.sm} {
          border-radius: 8px;
        }

        @media ${customCss.breakpoints.lg} {
          img {
            filter: brightness(95%);
          }
          &:hover img,
          &:focus img {
            filter: brightness(100%);
          }
        }
      `}
    >
      <Picture
        alt=""
        url={url}
        sources={sources}
        loading={loading}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 1s;
          transform: scale3d(1, 1, 1);
          cursor: pointer;

          @media ${customCss.breakpoints.sm} {
            border-radius: 8px;
          }
        `}
      />
      {overlay && <ImageSlideOverlay>{overlay}</ImageSlideOverlay>}
    </div>
  );
}

export { ImageSlide };
